import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Dropdown, Tab, TabPane } from 'semantic-ui-react'
import i18n from '~/components/i18n'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { FarmersQuery } from '~/QueryHooks/Farmers'

const getDefaultTableComunHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Procurement month'),
    key: 'billingMonth',
    type: 'billingMonth'
  },
  {
    name: i18n.t('Date'),
    key: 'date',
    type: 'date',
    format: 'DD.MM.YYYY'
  },
  {
    name: i18n.t('Fat (in %)'),
    key: 'fat',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Protein (in %)'),
    key: 'protein',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Germ count (1000/ml)'),
    key: 'germCount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Cell count (1000/ml)'),
    key: 'cellCount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Inhibitor'),
    key: 'inhibitorLabel'
  },
  {
    name: i18n.t('Freezing point (in °C)'),
    key: 'freezingPoint',
    type: 'no-unit-number-no-value'
  },
  {
    name: i18n.t('Urea (mg/dl)'),
    key: 'urea',
    type: 'no-unit-number-no-value'
  }
]

const prefixes = ['daily-values', 'individual-values', 'average-values']

const MilkSamples:React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableComunHeaders())
  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    date: ''
  })

  const {
    data: milkSamples,
    isLoading: isLoadingSamples,
    isFetching: isFetchingSamples,
    refetch: fetchSamples
  } = FarmersQuery.getMilkSamples(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    }, dataFiltered.date !== '',
    prefixes[activeTabIndex])

  const {
    data: milkSampData,
    isLoading: isLoadingSamData
  } = FarmersQuery.getMilkSamplesData(id, dataFiltered)

  useEffect(() => {
    fetchSamples()
  }, [dataFiltered])

  useEffect(() => {
    if (milkSampData && !dataFiltered.date) {
      setDataFiltered({
        ...dataFiltered,
        date: milkSampData?.months[0]?.key
      })
    }
  }, [milkSampData])

  function changeKeyColumnMonth(key, changeKey) {
    const columns = [...tableColumnHeaders]
    const index = columns.findIndex(column => column.key === changeKey)
    if (index >= 0) {
      columns[index].key = key
    }
    return columns
  }

  function getFilteredTableColumns() {
    const dairyRemovedColumns = ['date', 'supplierNo', 'inhibitorLabel']
    //refactor these filters
    switch (activeTabIndex) {
      case 0:
        return changeKeyColumnMonth('samplingDate', 'billingMonth').filter(column => column.key !== 'inhibitorLabel')
      case 1:
        return changeKeyColumnMonth('samplingDate', 'billingMonth')
      case 2:
        return changeKeyColumnMonth('billingMonth', 'samplingDate').filter(column => !dairyRemovedColumns.find(key => column.key === key))
      default:
        return []
    }
  }

  function renderTable() {
    const filteredTableColumns = getFilteredTableColumns()
    return <TableComponent
      isFetchingData={isFetchingSamples}
      animated
      tableColumnHeaders={filteredTableColumns}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderPanes() {
    const panes = [
      {
        menuItem: t('Daily samples')
      },
      {
        menuItem: t('All individual values')
      },
      {
        menuItem: t('Dairy average values')
      }
    ]

    return <Tab
      activeIndex={activeTabIndex}
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      onTabChange={(e, data) => {
        setDataFiltered({
          ...dataFiltered,
          date: milkSampData?.months[0]?.key
        })
        setActiveTabIndex(data.activeIndex as number)
      }}
      panes={panes.map((pane, index) => {
        return {
          menuItem: pane.menuItem,
          pane: <TabPane
            active={index === activeTabIndex}
            style={{ border: 'none', marginBottom: 0, borderRadius: 15, minHeight: 400, background: 'transparent' }}
            key={pane.menuItem}
          >
            {activeTabIndex <= 1 && renderDataFilter()}
            {renderTable()}
          </TabPane>
        }
      })}
    />
  }

  function renderDataFilter() {
    return <Dropdown
      placeholder={t('Procurement month')}
      options={milkSampData?.months?.map(month => ({ value: month.key, text: month.label })) || []}
      selectOnBlur={false}
      style={{ marginBottom: '1em' }}
      value={dataFiltered.date}
      selection
      onChange={(_, data) => setDataFiltered({
        ...dataFiltered,
        date: data.value as string
      })}
    />
  }
 
  const { filtered, rows } = milkSamples ? milkSamples : { filtered: 0, rows: [] };

  return <Layout
    title={t('Milk samples')}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    useSpinner
    isBusy={isLoadingSamples || isLoadingSamData}
  >
    {renderPanes()}
  </Layout>
}
export default MilkSamples